import React, { memo, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Text from '../../elements/Text';
import Box from '../../elements/Box';

const onKeyDown = (event, onEscKeyPress) => {
  if (event.keyCode === 27) {
    event.preventDefault();
    onEscKeyPress();
  }
};

const calculePadding = (index, prime = false, padding) => {
  if (prime) {
    return index % 2 === 1 ? padding : '0';
  }
  return index % 2 === 0 ? padding : '0';
};

/**
 * Determine the height of the List depending of the cant of items that you want to show in the list per scroll.
 * NOTE: check this Trello Card:(https://trello.com/c/zQNE4PXY/1291-featured-destination-airbnb-featured-listings-in-the-left-other-top-places-there-are-less-offers-there-is-a-lot-of-space-between)
 * @param {Number} total
 * @param {Number} visible
 */
const calculeListHeight = (total, visible) => (total <= visible ? 'auto' : '');

/**
 * Determine when to apply Settings for the Selected Item
 */
const getSelectedItemSettings = (index, focusOn, selectedItemSettings) => {
  return index === focusOn ? selectedItemSettings : null;
};

const FareListTwoColumns = (props) => {
  const {
    ItemComponentInstance,
    itemSettings,
    selectedItemSettings,
    itemsData,
    oneColumn,
    onEscKeyPress,
    focusReferences,
    columnNumbers,
    ariaLabelledBy,
    fareListTwoColumnsItem,
    itemsVisible,
    focusOn,
    paddingItem,
    gutters,
    showCallToActionInteractive,
    onItemActive,
    readAccessibilityMessage,
    fareItemInteractive,
    selectedItem,
    getSelectedItemProps
  } = props;

  const { direction, theme, translate } = useContext(Context);
  const [itemActive, setItemActive] = useState(null);
  const [className, style] = useStyles(props, { direction, theme });
  const itemsSize = itemsData.length;
  let lgWidth = oneColumn ? '12' : '6';
  if (columnNumbers) {
    lgWidth = `${12 / columnNumbers}`;
  }

  useEffect(() => {
    setItemActive(focusOn);
  }, [focusOn]);

  const handleItemActive = (data, makeAction = true) => {
    if (fareItemInteractive) {
      setItemActive(data.index);
      onItemActive(data, makeAction);
    }
  };
  let newItemSettings = { ...itemSettings };
  return (
    <>
      <Row
        height={calculeListHeight(itemsData.length, itemsVisible)}
        className={`FareListTwoColumns ${className}`}
        style={style}
        role="grid"
        ariaLabelledBy={ariaLabelledBy}
        onKeyDown={(e) => onKeyDown(e, onEscKeyPress)}
        gutters={gutters}
        tabIndex={readAccessibilityMessage ? '0' : '-1'}
      >
        <Row role="rowgroup" style={{ width: '100%' }}>
          {itemsData.map((data, index) => {
            newItemSettings = { ...itemSettings };
            if (selectedItem && getSelectedItemProps) {
              newItemSettings = getSelectedItemProps(data, newItemSettings);
            }
            const newData = { ...data };
            newData.navigationInfo =
              direction === 'ltr' ? `${index + 1}/${itemsSize}` : `${itemsSize}/${index + 1}`;
            const hideCallToActionOnFocusLeave =
              showCallToActionInteractive && itemActive === itemsData[itemsData.length - 1].index;
            return (
              <Col
                lg={lgWidth}
                paddingRight={oneColumn ? '0' : calculePadding(index, false, paddingItem)}
                paddingLeft={oneColumn ? '0' : calculePadding(index, true, paddingItem)}
                className="fare-item-wrapper"
                role="row"
                id={`fare-item-${data.index}`}
                key={data.index}
                {...fareListTwoColumnsItem}
                {...getSelectedItemSettings(data.index, focusOn, selectedItemSettings)}
              >
                <Box role="gridcell" className="fr-gridcell">
                  <ItemComponentInstance
                    focusReferences={data.index === focusOn ? focusReferences : null}
                    focusElement={focusOn === data.index}
                    {...newItemSettings}
                    onClick={() => {
                      handleItemActive(data, false);
                    }}
                    onFocus={() => {
                      if (data.index !== props.focusOn) {
                        handleItemActive(data);
                      }
                    }}
                    showCallToAction={
                      showCallToActionInteractive && fareItemInteractive
                        ? itemActive === data.index
                        : newItemSettings.showCallToAction
                    }
                    hideCallToActionOnFocusLeave={hideCallToActionOnFocusLeave}
                    data={newData}
                  />
                </Box>
              </Col>
            );
          })}
        </Row>
      </Row>
      {readAccessibilityMessage && (
        <Text id={ariaLabelledBy} className="fr-sr-only">
          {translate(
            'cmp-fare-list-column-screen-reader-message',
            'When you browser over the list items on top direction use tab key to access the book now button'
          )}
        </Text>
      )}
    </>
  );
};

FareListTwoColumns.propTypes = {
  /**
   * index that will get the focusReference
   */
  focusOn: PropTypes.number,
  /**
   * settings of the item selected
   */
  selectedItemSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * itemSettings object of settings
   */
  itemSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component
   */
  ItemComponentInstance: PropTypes.oneOfType([PropTypes.elementType, PropTypes.func]),
  /**
   * List items
   */
  itemsData: PropTypes.arrayOf(PropTypes.any),
  /**
   * Function to translate the component
   */
  oneColumn: PropTypes.bool,
  /**
   * Function to translate the component
   */
  columnNumbers: PropTypes.number,
  /**
   * on Esc Key press
   */
  onEscKeyPress: PropTypes.func,
  /**
   * ref
   */
  focusReferences: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * ariaLabelledBy of the list
   */
  ariaLabelledBy: PropTypes.string,
  /**
   * settings of fareListTwoColumnsItem
   */
  fareListTwoColumnsItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Define the cant of items visible in the current scroll of the list
   */
  itemsVisible: PropTypes.number,
  /**
   * padding applied to items (this is used in calculatePadding func check above)
   */
  paddingItem: PropTypes.string,
  /**
   * gutter applied to the list
   */
  gutters: PropTypes.bool,
  /**
   * Show callToAction when user has click on the ItemInstance
   */
  showCallToActionInteractive: PropTypes.bool,
  /**
   * function fired when fares is active
   */
  onItemActive: PropTypes.func
};
FareListTwoColumns.defaultProps = {
  focusOn: '',
  oneColumn: false,
  ItemComponentInstance: null,
  itemSettings: {},
  selectedItemSettings: {},
  itemsData: [],
  onEscKeyPress: () => {},
  focusReferences: () => {},
  columnNumbers: null,
  ariaLabelledBy: null,
  fareListTwoColumnsItem: {
    marginBottom: '2'
  },
  itemsVisible: 0,
  paddingItem: '1',
  gutters: false,
  showCallToActionInteractive: false,
  onItemActive: () => {}
};

FareListTwoColumns.componentName = 'FareListTwoColumns';

export default memo(
  FareListTwoColumns,
  (prevProps, nextProps) =>
    prevProps.itemsData === nextProps.itemsData &&
    prevProps.focusOn === nextProps.focusOn &&
    prevProps.onItemActive === nextProps.onItemActive &&
    prevProps.selectedItem === nextProps.selectedItem
);
